import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import PageHeader from '../components/ui/PageHeader';
import api from '../services/api';

const statusColors = { danger: '#ef5350', warning: '#ffb74d', info: '#42a5f5' };

function FinalHubPage() {
  const [stats, setStats] = useState({ employees: 0, projects: 0, tasks: 0, alerts: 0 });
  const [projects, setProjects] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dgRes, projRes, tasksRes] = await Promise.allSettled([
          api.dashboard.dg(),
          api.projects.getAll(),
          api.tasks.getAll({ done: false }),
        ]);

        if (dgRes.status === 'fulfilled' && dgRes.value?.stats) {
          const s = dgRes.value.stats;
          setStats({
            employees: s.total_employees || 0,
            projects: s.total_projects || 0,
            tasks: s.tasks_pending || 0,
            alerts: (!dgRes.value.employees ? 0 : dgRes.value.employees.filter(e => !e.present).length) || 0,
          });
        }

        if (projRes.status === 'fulfilled') {
          setProjects((projRes.value?.projects || []).slice(0, 4));
        }

        if (tasksRes.status === 'fulfilled') {
          const urgent = (tasksRes.value?.tasks || [])
            .filter(t => t.priority === 'urgente')
            .slice(0, 3)
            .map(t => ({
              type: 'danger',
              title: t.titre || t.title || '—',
              detail: `Priorité urgente · ${t.category || 'Admin'}`,
            }));
          setAlerts(urgent);
        }
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  const kpis = [
    { label: 'Collaborateurs', value: stats.employees, icon: '👥' },
    { label: 'Alertes actives', value: stats.alerts, icon: '⚠️' },
    { label: 'Projets suivis', value: stats.projects, icon: '📁' },
    { label: 'Tâches en attente', value: stats.tasks, icon: '✅' },
  ];

  const cards = [
    { title: 'Mon tableau de bord', subtitle: 'Accès direct aux modules clés', icon: '🧭', route: '/hub' },
    { title: 'RH centralisé', subtitle: 'Présence, tâches, performance', icon: '👥', route: '/tdb-employes' },
    { title: 'Mon TDB', subtitle: 'Suivi individuel et pointage', icon: '👤', route: '/tdb-individuel' },
  ];

  const quickLinks = [
    { label: 'Dashboard DG', icon: '🧭' },
    { label: 'Filiales', icon: '🏢' },
    { label: 'Projets', icon: '📋' },
    { label: 'Rapports', icon: '📊' },
  ];

  return (
    <MainLayout>
      <div style={{ display: 'grid', gap: 24 }}>
        <PageHeader title="Portail FHC" subtitle="Accès centralisé aux modules, aux rapports et aux alertes clés." />

        {loading && <div style={{ textAlign: 'center', color: '#C9A84C', padding: 20 }}>Chargement…</div>}

        {/* KPIs dynamiques */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 16 }}>
          {kpis.map(item => (
            <div key={item.label} style={{ background: '#0f1724', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18, padding: 18, minHeight: 110 }}>
              <div style={{ fontSize: 20 }}>{item.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 12 }}>{item.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>{item.label}</div>
            </div>
          ))}
        </div>

        {/* Cards navigation */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16 }}>
          {cards.map(card => (
            <div key={card.title} style={{ background: '#0f1724', border: '1px solid rgba(255,255,255,.08)', borderRadius: 20, padding: 22 }}>
              <div style={{ fontSize: 22, marginBottom: 12 }}>{card.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{card.title}</div>
              <div style={{ color: '#94a3b8', marginBottom: 14 }}>{card.subtitle}</div>
              <div style={{ color: '#C9A84C', fontSize: 12, fontWeight: 700 }}>{card.route}</div>
            </div>
          ))}
        </div>

        {/* Projets récents */}
        {projects.length > 0 && (
          <div style={{ display: 'grid', gap: 16 }}>
            <h2 style={{ margin: 0 }}>Projets récents</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
              {projects.map(p => (
                <div key={p.id} style={{ background: '#0f1724', border: '1px solid rgba(255,255,255,.08)', borderRadius: 16, padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.name || p.nom}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.status || p.statut}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#C9A84C' }}>{p.progress || 0}%</div>
                    <div style={{ width: 80, background: 'rgba(255,255,255,.06)', borderRadius: 999, height: 6, overflow: 'hidden', marginTop: 4 }}>
                      <div style={{ width: `${p.progress || 0}%`, height: '100%', background: '#C9A84C', borderRadius: 999 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Raccourcis */}
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Raccourcis</h2>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Accès rapide aux sections principales</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 14 }}>
            {quickLinks.map(link => (
              <div key={link.label} style={{ background: '#0f1724', border: '1px solid rgba(255,255,255,.08)', borderRadius: 16, padding: 18, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 18 }}>{link.icon}</span>
                <span style={{ fontWeight: 600 }}>{link.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Alertes dynamiques */}
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Alertes</h2>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Tâches urgentes en attente</span>
          </div>
          {alerts.length === 0 && !loading && (
            <div style={{ background: '#0f1724', borderRadius: 16, padding: 18, textAlign: 'center', color: '#66bb6a', fontSize: 13 }}>
              ✅ Aucune alerte urgente
            </div>
          )}
          <div style={{ display: 'grid', gap: 12 }}>
            {alerts.map((alert, i) => (
              <div key={i} style={{ background: '#0f1724', border: '1px solid rgba(255,255,255,.08)', borderRadius: 16, padding: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: statusColors[alert.type] || '#42a5f5', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700 }}>{alert.title}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{alert.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default FinalHubPage;
